import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearPosDisplayState,
  closePosCustomerDisplay,
  isPosDisplayWindowOpen,
  openPosCustomerDisplay,
  PosDisplayLineItem,
  PosDisplayPaymentStatus,
  publishPosDisplayState,
} from "../lib/posDisplaySync";

type UsePosCustomerDisplayArgs = {
  enabled: boolean;
  companyName: string;
  currencySymbol: string;
  customerName?: string | null;
  items: PosDisplayLineItem[];
  originalNet: number;
  discount: number;
  tax: number;
  total: number;
  paymentStatus: PosDisplayPaymentStatus;
  tenderLabel?: string | null;
  amountDue?: number | null;
  totalTendered?: number | null;
  changeDue?: number | null;
  amountPaid?: number | null;
};

export function usePosCustomerDisplay({
  enabled,
  companyName,
  currencySymbol,
  customerName,
  items,
  originalNet,
  discount,
  tax,
  total,
  paymentStatus,
  tenderLabel,
  amountDue,
  totalTendered,
  changeDue,
  amountPaid,
}: UsePosCustomerDisplayArgs) {
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);
  const autoOpenAttempted = useRef(false);

  const publish = useCallback(() => {
    if (!enabled) return;
    publishPosDisplayState({
      companyName,
      currencySymbol,
      customerName,
      items,
      originalNet,
      discount,
      tax,
      total,
      paymentStatus,
      tenderLabel: tenderLabel ?? null,
      amountDue: amountDue ?? null,
      totalTendered: totalTendered ?? null,
      changeDue: changeDue ?? null,
      amountPaid: amountPaid ?? null,
    });
  }, [
    enabled,
    companyName,
    currencySymbol,
    customerName,
    items,
    originalNet,
    discount,
    tax,
    total,
    paymentStatus,
    tenderLabel,
    amountDue,
    totalTendered,
    changeDue,
    amountPaid,
  ]);

  const openDisplay = useCallback(async (force = false) => {
    const result = await openPosCustomerDisplay({ force });
    setPopupBlocked(result.blocked);
    setDisplayOpen(result.opened);
    if (result.opened) {
      publish();
    }
    return result;
  }, [publish]);

  const closeDisplay = useCallback(() => {
    closePosCustomerDisplay();
    setDisplayOpen(false);
    setPopupBlocked(false);
    clearPosDisplayState({
      companyName,
      currencySymbol,
    });
  }, [companyName, currencySymbol]);

  // Auto-open once when enabled
  useEffect(() => {
    if (!enabled) {
      autoOpenAttempted.current = false;
      if (isPosDisplayWindowOpen()) {
        closeDisplay();
      }
      return;
    }

    if (autoOpenAttempted.current) return;
    autoOpenAttempted.current = true;

    (async () => {
      const result = await openPosCustomerDisplay();
      setPopupBlocked(result.blocked);
      setDisplayOpen(result.opened);
      if (result.opened) publish();
    })();
  }, [enabled, closeDisplay, publish]);

  // Keep display state in sync
  useEffect(() => {
    if (!enabled) return;
    publish();
  }, [enabled, publish]);

  // Reconnect poll — if window closed unexpectedly, show open button again
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      const open = isPosDisplayWindowOpen();
      setDisplayOpen(open);
      if (!open && !popupBlocked) {
        // keep blocked flag only after a failed open attempt
      }
    }, 2000);
    return () => window.clearInterval(id);
  }, [enabled, popupBlocked]);

  // Close on unmount if leaving POS
  useEffect(() => {
    return () => {
      // Do not force-close on remount/navigation within POS; only when leaving
    };
  }, []);

  return {
    popupBlocked,
    displayOpen,
    openDisplay,
    closeDisplay,
  };
}
